import * as React from 'react';
import moment from 'moment';
import "./ProfessionalItemContent.scss";
import ListAvatar from '../../../components/ListAvatar';
import { DayOfWeekEnum, ProfessionalAvailability, ProfessionalData } from '../../../models';
import { useSelector } from 'react-redux';
import { getGeneralSettings } from '../../../store/settings/selectors';
import { getSelectedBranch } from '../../../store/general/selectors';
import { useTranslations } from '../../../store/translations/hooks';
import { getWeekDayTranslationKey } from '../../../shared/utils';

export interface ProfessionalItemContentProps {
    professional: ProfessionalData,
}

const DAY_ORDER = [
    DayOfWeekEnum.Monday,
    DayOfWeekEnum.Tuesday,
    DayOfWeekEnum.Wednesday,
    DayOfWeekEnum.Thursday,
    DayOfWeekEnum.Friday,
    DayOfWeekEnum.Saturday,
    DayOfWeekEnum.Sunday,
];

function getActiveDays(availability: ProfessionalAvailability[]): DayOfWeekEnum[] {
    const today = moment().format('YYYY-MM-DD');
    const active = [...availability]
        .filter(a => a.from <= today)
        .sort((a, b) => b.from.localeCompare(a.from))[0];
    if (!active) return [];
    const unique = new Set(active.timeSlots.map(s => s.dayOfWeek));
    return DAY_ORDER.filter(d => unique.has(d));
}

const ProfessionalItemContent: React.FC<ProfessionalItemContentProps> = (props) => {
    const { professional } = props;
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);
    const branches = generalSettings?.branches ?? {};
    const selectedBranch = useSelector(getSelectedBranch);

    const isInSelectedBranch = React.useMemo(() => {
        const numBranches = Object.keys(branches).length;
        if (!selectedBranch || numBranches <= 1) return true;
        const branchAvailMap = professional.branchTimeAvailability ?? {};
        if (Object.keys(branchAvailMap).length === 0) return false; // no branch availability defined → not active in any branch
        return getActiveDays(branchAvailMap[selectedBranch] ?? []).length > 0;
    }, [selectedBranch, branches, professional]);

    const branchEntries = Object.entries(professional.branchTimeAvailability ?? {});
    const hasPerBranchAvailability = branchEntries.length > 0;

    const availabilityLines: { label?: string; days: DayOfWeekEnum[] }[] = [];

    if (hasPerBranchAvailability) {
        for (const [branchId, avail] of branchEntries) {
            const days = getActiveDays(avail);
            if (days.length > 0) {
                availabilityLines.push({ label: branches[branchId]?.displayName ?? branchId, days });
            }
        }
    } else if (professional.timeAvailability?.length) {
        const days = getActiveDays(professional.timeAvailability);
        if (days.length > 0) {
            availabilityLines.push({ days });
        }
    }

    return (
        <div className={`professionalItemContent${isInSelectedBranch ? '' : ' professionalItemContent--inactive'}`}>
            <ListAvatar
                src={professional.photoUrl}
                alt={professional.name}
                displayName={professional.name}
                email={professional.email}
                colorCode={professional.colorCode}
            />
            <div
                className="professionalColorIndicator"
                style={{ backgroundColor: professional.colorCode }}
            />
            <div className="professionalInfo">
                <span className="professionalName">{professional.name}</span>
                {availabilityLines.map((line, i) => (
                    <span key={i} className="professionalAvailability">
                        {line.label && <span className="professionalAvailabilityBranch">{line.label}: </span>}
                        {line.days.map(d => t(getWeekDayTranslationKey(d)).substring(0, 3)).join(' ')}
                    </span>
                ))}
            </div>
        </div>
    );
}

export default ProfessionalItemContent;
